import { useState, useCallback, useMemo } from "react";
import AsyncSelect from 'react-select/async';
import toast from "react-hot-toast";
import { CircularProgress } from "@mui/material";

import { fetchPostcodeAddress, fetchPostcodeAddressList } from "../../service";

const PostcodeSearch = ({ setValue }) => {
    const [loading, setLoading] = useState(false);

    const isValidUKPostcode = (postcode) => {
        const trimmed = postcode.trim().toUpperCase();
        const postcodeRegex = /^[A-Z]{1,2}[0-9R][0-9A-Z]? ?[0-9][A-Z]{2}$/;
        return postcodeRegex.test(trimmed);
    };

    const handleAddressChange = (selectedOption) => {
        if (selectedOption) {
            getPostcodeAddressDetails(selectedOption.value);
        }
    };

    const getPostcodeAddressList = useCallback(async (inputValue, callback) => {
        try {
            const trimmedInput = inputValue.trim().toUpperCase();
            if (isValidUKPostcode(trimmedInput)) {
                const response = await fetchPostcodeAddressList(trimmedInput);
                const addresses = response?.list || [];

                const options = addresses.map((address) => ({
                    value: address,
                    label: `${address?.StreetAddress?.toUpperCase()}, ${address?.Place?.toUpperCase()}`,
                }));

                callback(options);
            } else {
                callback([]);
            }
        } catch (error) {
            console.error("Error fetching address list:", error);
            toast.error("Failed to fetch address list.");
            callback([]);
        }
    }, []);

    const getPostcodeAddressDetails = async (value) => {
        try {
            setLoading(true);
            const query = `${value.StreetAddress}, ${value.Place}`;
            const response = await fetchPostcodeAddress(query);

            if (response.status === "Success") {
                const { street_name_and_number, postal_town, postcode, location } = response.details;
                setValue("ftm_street_address", street_name_and_number);
                setValue("ftm_place", postal_town);
                setValue("ftm_postcode", postcode);
                setValue("ftm_latitude", location?.lat);
                setValue("ftm_longitude", location?.lng);
            } else {
                toast.error(response.message || "Failed to fetch address details.");
            }
        } catch (error) {
            console.error("Error fetching address details:", error);
            toast.error("Error fetching address details.");
        } finally {
            setLoading(false);
        }
    };

    const selectStyles = useMemo(() => ({
        control: (base) => ({
            ...base,
            width: "100%",
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected
                ? `#8bd68fff`
                : state.isFocused
                    ? "#8bd68fff"
                    : "#ffffff",
            color: state.isSelected || state.isFocused ? "#fff" : "#333",
        }),
    }), []);

    return (
        <div className="mt-1 mb-3">
            <strong>Enter your postcode</strong>
            <div className="d-flex align-items-center gap-2 mt-1">
                <div className="flex-grow-1">
                    <AsyncSelect
                        isClearable
                        cacheOptions
                        defaultOptions
                        loadOptions={getPostcodeAddressList}
                        onChange={handleAddressChange}
                        styles={selectStyles}
                    />
                </div>

                {loading && (
                    <CircularProgress size={24} color="success" />
                )}
            </div>
        </div>
    );
};

export default PostcodeSearch;